import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import Dropdown from 'components/Dropdown';
import Spinner from 'view/components/Spinner';
import { fetchAkas } from 'reducers/akaReducer';
import './AkaDropdownButton.css';

// Normalize numeric-like values by stripping leading zeros for reliable comparisons
const normalizeNumLike = (v) => {
	if (v === null || v === undefined) return '';
	const s = String(v).trim();
	// keep '0' as '0', otherwise strip leading zeros
	const stripped = s.replace(/^0+/, '');
	return stripped === '' ? '0' : stripped;
};

const AkaDropdownMenu = ({
	akas = [],
	currentBin,
	currentBlock,
	currentLot,
	onSelect,
}) => {
	// Separate additional bins from other AKAs
	const additionalBins = akas.filter(
		(aka) => aka.linkageType === 'additional_bin',
	);
	const otherAkas = akas.filter((aka) => aka.linkageType !== 'additional_bin');

	if (!otherAkas?.length && !additionalBins?.length) {
		return (
			<div className="text-muted px-2 py-1 small">
				No alternate addresses found.
			</div>
		);
	}

	// Sort by two-level priority: Shared BIN first, then Others (excluding additional_bin)
	const priority = { shared_bin: 1 };
	const sorted = [...otherAkas].sort((a, b) => {
		const pa = priority[a.linkageType] || 99;
		const pb = priority[b.linkageType] || 99;
		if (pa !== pb) return pa - pb;
		// Secondary: BIN match first
		const aHasBin = !!a.bin;
		const bHasBin = !!b.bin;
		if (aHasBin !== bHasBin) return aHasBin ? -1 : 1;
		// Finally, alphabetical by address
		return String(a.address || '').localeCompare(String(b.address || ''));
	});

	// Sort additional bins alphabetically
	const sortedAdditionalBins = [...additionalBins].sort((a, b) => {
		return String(a.address || '').localeCompare(String(b.address || ''));
	});

	return (
		<>
			<div className="aka-dropdown-list">
				{sorted.map((aka) => {
					const isDiffBin =
						currentBin &&
						aka.bin &&
						normalizeNumLike(aka.bin) !== normalizeNumLike(currentBin);
					const isSameBlockLot =
						currentBlock &&
						currentLot &&
						aka.block &&
						aka.lot &&
						normalizeNumLike(aka.block) === normalizeNumLike(currentBlock) &&
						normalizeNumLike(aka.lot) === normalizeNumLike(currentLot);

					// Determine badge: shared when linkageType is shared_bin OR bins match
					const isSharedBin =
						aka.linkageType === 'shared_bin' ||
						(aka.bin &&
							currentBin &&
							normalizeNumLike(aka.bin) === normalizeNumLike(currentBin));
					const badgeClass = isSharedBin ? 'aka-badge-shared' : 'aka-badge-other';
					const badgeLabel = isSharedBin ? 'Shared BIN' : 'Other';
					const key = aka.property_id || aka.address;
					const handleClick = () => {
						if (onSelect) onSelect(aka);
					};
					return (
						<div
							key={key}
							className="aka-dropdown-item text-decoration-none d-flex align-items-center justify-content-between"
							role="button"
							tabIndex={0}
							onClick={handleClick}
							onKeyDown={(e) => {
								if (e.key === 'Enter') handleClick();
							}}
						>
							<div className="aka-left flex-grow-1">
								<div
									className="aka-line-1"
									title={aka.address}
								>
									{aka.address}
								</div>
								<div className="aka-line-2">
									<span
										className={`aka-bracket ${!isSameBlockLot ? 'aka-red' : ''}`}
									>
										[Block/Lot -{' '}
										{aka.block ? aka.block.toString().padStart(5, '0') : '—'}/
										{aka.lot ? aka.lot.toString().padStart(4, '0') : '—'}]
									</span>
									<span className="aka-sep" />
									<span className={`aka-bracket ${isDiffBin ? 'aka-red' : ''}`}>
										[BIN - {aka.bin || '—'}]
									</span>
								</div>
							</div>
							<div className="aka-right ms-2 d-flex align-items-center justify-content-center">
								<span
									className={`aka-badge ${badgeClass}`}
									title={aka.source ? `Source: ${aka.source}` : ''}
								>
									{badgeLabel}
								</span>
							</div>
						</div>
					);
				})}
			</div>
			{sortedAdditionalBins.length > 0 && (
				<div className="aka-additional-bins-section">
					<div className="aka-additional-bins-header">
						<span>Additional BINs for Building</span>
					</div>
					<div className="aka-additional-bins-list">
						{sortedAdditionalBins.map((aka) => {
							const isDiffBin =
								currentBin &&
								aka.bin &&
								normalizeNumLike(aka.bin) !== normalizeNumLike(currentBin);
							const isSameBlockLot =
								currentBlock &&
								currentLot &&
								aka.block &&
								aka.lot &&
								normalizeNumLike(aka.block) ===
									normalizeNumLike(currentBlock) &&
								normalizeNumLike(aka.lot) === normalizeNumLike(currentLot);

							const key = aka.property_id || aka.address;
							const handleClick = () => {
								if (onSelect) onSelect(aka);
							};
							return (
								<div
									key={key}
									className="aka-dropdown-item text-decoration-none d-flex align-items-center justify-content-between"
									role="button"
									tabIndex={0}
									onClick={handleClick}
									onKeyDown={(e) => {
										if (e.key === 'Enter') handleClick();
									}}
								>
									<div className="aka-left flex-grow-1">
										<div
											className="aka-line-1"
											title={aka.address}
										>
											{aka.address}
										</div>
										<div className="aka-line-2">
											<span
												className={`aka-bracket ${!isSameBlockLot ? 'aka-red' : ''}`}
											>
												[Block/Lot -{' '}
												{aka.block
													? aka.block.toString().padStart(5, '0')
													: '—'}
												/{aka.lot ? aka.lot.toString().padStart(4, '0') : '—'}]
											</span>
											<span className="aka-sep" />
											<span
												className={`aka-bracket ${isDiffBin ? 'aka-red' : ''}`}
											>
												[BIN - {aka.bin || '—'}]
											</span>
										</div>
									</div>
									<div className="aka-right ms-2 d-flex align-items-center justify-content-center">
										<span
											className="aka-badge aka-badge-additional"
											title={aka.source ? `Source: ${aka.source}` : ''}
										>
											Additional BIN
										</span>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}
		</>
	);
};

AkaDropdownMenu.propTypes = {
	akas: PropTypes.arrayOf(
		PropTypes.shape({
			property_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			address: PropTypes.string,
			bin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			block: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			lot: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			linkageType: PropTypes.string,
			source: PropTypes.string,
		}),
	),
	currentBin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	currentBlock: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	currentLot: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const handleAkaSelect = (aka, history) => {
	const address = encodeURIComponent(aka.address || '');
	const params = new URLSearchParams();
	params.set('aka', '1');
	if (aka.bin) params.set('bin', aka.bin);
	if (aka.block) params.set('block', aka.block);
	if (aka.lot) params.set('lot', aka.lot);
	// Prefer explicit numeric borough on AKA, else derive from address text
	if (aka.borough) {
		params.set('borough', String(aka.borough));
	} else if (aka.address) {
		const parts = aka.address.split(',');
		if (parts.length >= 2) {
			const bName = parts[1].trim().toUpperCase();
			const boroughMap = {
				MANHATTAN: 1,
				BRONX: 2,
				BROOKLYN: 3,
				QUEENS: 4,
				'STATEN ISLAND': 5,
			};
			if (boroughMap[bName]) params.set('borough', String(boroughMap[bName]));
		}
	}
	history.push(`/property/${address}/overview?${params.toString()}`);
};

const AkaDropdownButton = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const {
		propertyId,
		akas = [],
		akaOwnerPropertyId,
		currentBlock,
		currentLot,
		currentBin,
		isLoading,
		hasError,
	} = useSelector((state) => ({
		propertyId: state.property.propertyData?.id,
		akas: state.aka?.akas,
		akaOwnerPropertyId: state.aka?.propertyId,
		currentBlock: state.property.propertyData?.block,
		currentLot: state.property.propertyData?.lot,
		currentBin: state.property.propertyData?.bin,
		isLoading: state.aka?.isLoading,
		hasError: state.aka?.hasError,
	}));
	const lastRequestRef = useRef({ propertyId: null });

	useEffect(() => {
		if (!propertyId) return; // must have a property before fetching
		const firstForThisProperty =
			lastRequestRef.current.propertyId !== propertyId;
		if (!firstForThisProperty) return; // nothing new to do

		lastRequestRef.current = { propertyId };
		dispatch(fetchAkas(propertyId));
	}, [propertyId, dispatch]);

	// Only show AKAs if they belong to the currently viewed property; else show empty to avoid stale data
	const visibleAkas = akaOwnerPropertyId === propertyId ? akas : [];

	return (
		<Dropdown
			buttonText={'Aka'}
			menuChild={
				<div className="aka-menu">
					<div className="aka-menu-header">
						<span>Also Known As</span>
					</div>
					{/* Legend for linkage badges */}
					{isLoading && akaOwnerPropertyId !== propertyId ? (
						<div className="aka-dropdown-list aka-loading">
							<Spinner
								size="xl"
								text="Loading alternate addresses..."
								color="dark"
							/>
						</div>
					) : isLoading ? (
						<div className="aka-dropdown-list aka-loading">
							<Spinner
								size="xl"
								text="Loading alternate addresses..."
								color="dark"
							/>
						</div>
					) : hasError ? (
						<div className="aka-dropdown-list aka-loading text-danger small">
							Failed to load alternate addresses. Please retry.
						</div>
					) : (
						<AkaDropdownMenu
							akas={visibleAkas}
							currentBin={currentBin}
							currentBlock={currentBlock}
							currentLot={currentLot}
							onSelect={(aka) => handleAkaSelect(aka, history)}
						/>
					)}
					<div className="aka-disclaimer">
						This data is sourced from the NYC Department of Buildings datasets.
						Accuracy is based on available public data.
					</div>
				</div>
			}
			className="aka-dd-container"
			buttonClass="aka-dd-btn"
			menuClass="aka-dd-menu"
		/>
	);
};

export default AkaDropdownButton;
